import { Alert, Button, Card, Progress, Space, Spin, Tag, Tooltip, Typography, theme } from "antd";
import { Bot, RefreshCw, ThumbsUp, AlertTriangle, Clock, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnalyzeWithAI, useGetAIAnalysis, useIsAIGradingConfigured } from "@hooks";
import { AIGradingAnalysis } from "@types";
import { motion, AnimatePresence } from "framer-motion";
import { useMemo } from "react";

const { useToken } = theme;

interface AIGradingPanelProps {
    /** The exam instance question ID to analyze */
    examInstanceQuestionId?: string;
    /** Maximum points for this question */
    maxPoints?: number;
    /** Whether the student answer is empty */
    hasNoAnswer?: boolean;
    /** Callback when "Apply Suggestion" is clicked */
    onApplySuggestion?: (suggestedPoints: number) => void;
}

/**
 * Panel component for AI-assisted essay grading
 * Shows AI analysis results with confidence, explanation, and option to apply suggestion
 */
export const AIGradingPanel = ({
    examInstanceQuestionId,
    maxPoints,
    hasNoAnswer = false,
    onApplySuggestion
}: AIGradingPanelProps) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const analyzeWithAI = useAnalyzeWithAI();
    const { data: existingAnalysis, isLoading: isLoadingAnalysis } = useGetAIAnalysis(examInstanceQuestionId);
    const { data: isConfigured, isLoading: isCheckingConfig } = useIsAIGradingConfigured();

    // Use mutation result if available, otherwise use existing analysis from query
    const analysis: AIGradingAnalysis | null | undefined = analyzeWithAI.data || existingAnalysis;

    const handleAnalyze = () => {
        if (examInstanceQuestionId) {
            analyzeWithAI.mutate(examInstanceQuestionId);
        }
    };

    const handleApplySuggestion = () => {
        if (analysis && onApplySuggestion) {
            onApplySuggestion(analysis.suggestedPoints);
        }
    };

    // Format the date for display
    const formattedDate = useMemo(() => {
        if (!analysis?.dateCreated) return null;
        try {
            return new Date(analysis.dateCreated).toLocaleString();
        } catch {
            return analysis.dateCreated;
        }
    }, [analysis?.dateCreated]);

    // Determine confidence color
    const getConfidenceColor = (confidence: number): string => {
        if (confidence >= 85) return "#52c41a"; // green
        if (confidence >= 70) return "#faad14"; // yellow/warning
        return "#ff4d4f"; // red
    };

    // Don't render if AI grading is not configured for this tenant
    if (isCheckingConfig) {
        return null; // Don't show anything while checking
    }

    if (!isConfigured) {
        return null; // Hide panel if not configured
    }

    // Handle empty answer case
    if (hasNoAnswer) {
        return (
            <Card 
                size="small" 
                style={{ marginTop: 16, background: token.colorBgLayout }}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Space>
                        <Bot size={20} />
                        <Typography.Text strong>{t("aiGrading.title")}</Typography.Text>
                    </Space>
                    <Alert
                        message={t("aiGrading.noAnswer")}
                        type="info"
                        showIcon
                    />
                </Space>
            </Card>
        );
    }

    // Loading state
    if (isLoadingAnalysis) {
        return (
            <Card size="small" style={{ marginTop: 16, background: token.colorBgLayout }}>
                <Space direction="vertical" style={{ width: "100%", alignItems: "center" }}>
                    <Spin />
                    <Typography.Text type="secondary">{t("aiGrading.loading")}</Typography.Text>
                </Space>
            </Card>
        );
    }

    return (
        <Card 
            size="small" 
            style={{ marginTop: 16, background: token.colorBgLayout }}
            title={
                <Space>
                    <Bot size={20} />
                    <Typography.Text strong>{t("aiGrading.title")}</Typography.Text>
                </Space>
            }
        >
            <AnimatePresence mode="wait">
                {!analysis ? (
                    // No analysis yet - show analyze button
                    <motion.div
                        key="no-analysis"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Typography.Text type="secondary">
                                {t("aiGrading.description")}
                            </Typography.Text>
                            <Button
                                type="primary"
                                icon={<Bot size={16} />}
                                loading={analyzeWithAI.isPending}
                                onClick={handleAnalyze}
                                block
                            >
                                {t("aiGrading.analyzeButton")}
                            </Button>
                        </Space>
                    </motion.div>
                ) : (
                    // Show analysis results
                    <motion.div
                        key="analysis"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                    >
                        <Space direction="vertical" style={{ width: "100%" }} size="middle">
                            {/* Low confidence warning */}
                            {analysis.isLowConfidence && (
                                <Alert
                                    message={t("aiGrading.lowConfidenceWarning")}
                                    type="warning"
                                    showIcon
                                    icon={<AlertTriangle size={16} />}
                                />
                            )}

                            {/* Points and confidence row */}
                            <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
                                <div style={{ minWidth: 120 }}>
                                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                                        {t("aiGrading.suggestedPoints")}
                                    </Typography.Text>
                                    <Typography.Title level={3} style={{ margin: 0 }}>
                                        {analysis.suggestedPoints}
                                        {maxPoints && (
                                            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                                                {" / "}{maxPoints}
                                            </Typography.Text>
                                        )}
                                    </Typography.Title>
                                </div>
                                <div style={{ width: 110, textAlign: "center" }}>
                                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                                        {t("aiGrading.confidence")}
                                    </Typography.Text>
                                    <Tooltip title={`${analysis.confidencePercentage}% ${t("aiGrading.confidence").toLowerCase()}`}>
                                        <Progress
                                            type="circle"
                                            percent={analysis.confidencePercentage}
                                            size={50}
                                            strokeColor={getConfidenceColor(analysis.confidencePercentage)}
                                            format={(percent) => `${percent}%`}
                                        />
                                    </Tooltip>
                                </div>
                            </div>

                            {/* Explanation */}
                            <div>
                                <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
                                    {t("aiGrading.explanation")}
                                </Typography.Text>
                                <Typography.Paragraph 
                                    style={{ 
                                        margin: 0, 
                                        background: token.colorBgContainer, 
                                        padding: 12, 
                                        borderRadius: 6,
                                        border: `1px solid ${token.colorBorderSecondary}`
                                    }}
                                >
                                    {analysis.explanation}
                                </Typography.Paragraph>
                            </div>

                            {/* Metadata row */}
                            <Space size="small" wrap>
                                {formattedDate && (
                                    <Tag icon={<Clock size={12} />}>
                                        {formattedDate}
                                    </Tag>
                                )}
                                {analysis.requestedByUserName && (
                                    <Tag icon={<User size={12} />}>
                                        {analysis.requestedByUserName}
                                    </Tag>
                                )}
                            </Space>

                            {/* Action buttons */}
                            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                                <Button
                                    icon={<RefreshCw size={14} />}
                                    onClick={handleAnalyze}
                                    loading={analyzeWithAI.isPending}
                                >
                                    {t("aiGrading.reanalyzeButton")}
                                </Button>
                                {onApplySuggestion && (
                                    <Button
                                        type="primary"
                                        icon={<ThumbsUp size={14} />}
                                        onClick={handleApplySuggestion}
                                    >
                                        {t("aiGrading.applySuggestion")}
                                    </Button>
                                )}
                            </Space>
                        </Space>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Error state */}
            {analyzeWithAI.isError && (
                <Alert
                    message={t("aiGrading.error")}
                    description={analyzeWithAI.error?.message}
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                />
            )}
        </Card>
    );
};
